import type { CSSProperties } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';

/**
 * Skeleton primitives — pure presentational shimmer placeholders.
 *
 * The animated "premium sheen" lives in the `.skeleton` CSS class
 * (`packages/ui/src/styles/tokens.css`): a token-coloured base with a light
 * sheen that sweeps left→right, frozen automatically under
 * `prefers-reduced-motion`. All primitives are `aria-hidden` — wrap a loading
 * region in `role="status"` (with an sr-only label) at the page level so
 * assistive tech announces the load without reading every placeholder.
 */

interface SkeletonProps {
  className?: string;
  /** Pill shape (avatars, dots). */
  rounded?: boolean;
  style?: CSSProperties;
}

/** A single shimmer block. Size it with `className` (Tailwind h-/w-) or `style`. */
export function Skeleton({ className, rounded = false, style }: SkeletonProps) {
  return (
    <div
      className={cn('skeleton h-4', rounded && 'rounded-full', className)}
      style={style}
      aria-hidden
    />
  );
}

interface SkeletonTextProps {
  /** Number of text lines. */
  lines?: number;
  className?: string;
  lineClassName?: string;
}

/** Stacked text lines; the last line is shortened to mimic a paragraph. */
export function SkeletonText({ lines = 3, className, lineClassName }: SkeletonTextProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-3', lines > 1 && i === lines - 1 && 'w-3/5', lineClassName)}
        />
      ))}
    </div>
  );
}

interface SkeletonCircleProps {
  /** Diameter in px. */
  size?: number;
  className?: string;
}

/** Circular placeholder for avatars / icon slots. */
export function SkeletonCircle({ size = 40, className }: SkeletonCircleProps) {
  return <Skeleton rounded className={className} style={{ width: size, height: size }} />;
}

interface SkeletonCardProps {
  className?: string;
  /** Body text lines below the header row. */
  lines?: number;
  /** Render the leading avatar circle in the header. */
  withAvatar?: boolean;
}

/** A card-shaped placeholder: avatar + title/subtitle header and body lines. */
export function SkeletonCard({ className, lines = 3, withAvatar = true }: SkeletonCardProps) {
  return (
    <div
      className={cn(
        'rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-1)]',
        className
      )}
      aria-hidden
    >
      <div className="flex items-center gap-3">
        {withAvatar && <SkeletonCircle size={40} />}
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      {lines > 0 && <SkeletonText className="mt-4" lines={lines} />}
    </div>
  );
}

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
  /** Render an uppercase header row of placeholders. */
  withHeader?: boolean;
}

/** A table-shaped placeholder inside a bordered card surface. */
export function SkeletonTable({
  rows = 6,
  columns = 4,
  className,
  withHeader = true,
}: SkeletonTableProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[14px] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-1)]',
        className
      )}
      aria-hidden
    >
      {withHeader && (
        <div
          className="flex items-center gap-4 border-b border-[var(--border)] bg-[var(--surface-2)] px-4 py-3"
        >
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton key={c} className="h-3 flex-1" />
          ))}
        </div>
      )}
      <div className="flex flex-col">
        {Array.from({ length: rows }).map((_row, r) => (
          <div
            key={r}
            className="flex items-center gap-4 border-b border-[var(--border)] px-4 py-3.5 last:border-b-0"
          >
            {Array.from({ length: columns }).map((_col, c) => (
              <Skeleton key={c} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
